import { PointerEvent as ReactPointerEvent, memo, useCallback } from 'react';

import {
  SimNode,
  truncateLabel,
} from '@/components/context-graph-viewer/helpers/graph-types';

interface GraphNodeProps {
  /** Passed to handlers only — render from the primitive props below, since
   * SimNode fields are mutated in place by the simulation/data updates and
   * memo would miss changes read off the object. */
  node: SimNode;
  label: string;
  radius: number;
  color: string;
  visible: boolean;
  active: boolean;
  isFocusNode: boolean;
  labelVisible: boolean;
  onHover: (id: string | null) => void;
  onPointerDown: (event: ReactPointerEvent, node: SimNode) => void;
  onEnterFocus: (id: string) => void;
  registerElement: (id: string, element: SVGGElement | null) => void;
}

// Memoized so hover/selection/search/timeline changes only re-render the
// nodes whose props actually changed (~2-10 instead of the whole scene).
// Positions flow through registerElement + updatePositions, not props. Keep
// the rendered DOM structure identical to what export-image expects (it
// pairs elements by querySelectorAll index).
export const GraphNode = memo(function GraphNode({
  node,
  label,
  radius,
  color,
  visible,
  active,
  isFocusNode,
  labelVisible,
  onHover,
  onPointerDown,
  onEnterFocus,
  registerElement,
}: GraphNodeProps) {
  const setRef = useCallback(
    (element: SVGGElement | null) => registerElement(node.id, element),
    [registerElement, node.id],
  );

  return (
    <g
      ref={setRef}
      className='cursor-pointer transition-opacity duration-200'
      style={{
        opacity: visible ? (active ? 1 : 0.15) : 0,
        pointerEvents: visible ? undefined : 'none',
      }}
      onPointerEnter={() => onHover(node.id)}
      onPointerLeave={() => onHover(null)}
      onDoubleClick={() => onEnterFocus(node.id)}
      onPointerDown={(event) => onPointerDown(event, node)}
    >
      {isFocusNode ? (
        <circle
          r={radius + 5}
          fill='none'
          stroke={color}
          strokeOpacity={0.35}
          strokeWidth={3}
        />
      ) : null}
      <circle
        r={radius}
        fill={color}
        fillOpacity={0.9}
        stroke='var(--background)'
        strokeWidth={1.5}
      />
      {labelVisible ? (
        <text
          x={radius + 6}
          y={4}
          fontSize={11}
          fill='var(--foreground)'
          fillOpacity={0.85}
          stroke='var(--background)'
          strokeWidth={3}
          strokeOpacity={0.65}
          paintOrder='stroke'
          className='pointer-events-none'
        >
          {truncateLabel(label)}
        </text>
      ) : null}
    </g>
  );
});
